import React, {
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { BuildingOffice2Icon } from '@heroicons/react/24/outline';
import axios from 'axios';
import { useSnackbar } from 'notistack';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import { AllAuthUserProfile, mintToken } from '../../auth/profileApi';
import { startLogin } from '../../auth/auth';
import {
  getActiveProfiles,
  useAllAuthProfiles,
} from '../../hooks/useAllAuthProfiles';
import Button from '../core/Button';
import { useLogout } from '../../hooks/useLogout';
import useUserStore from '../../store/user';

const messages = defineMessages({
  heading: {
    id: 'profile_selector_heading',
    defaultMessage: 'Choose profile',
  },
  loading: {
    id: 'profile_selector_loading',
    defaultMessage: 'Loading profiles…',
  },
  automaticSelection: {
    id: 'profile_selector_automatic_selection',
    defaultMessage: 'Signing you in as {role}…',
  },
  selecting: {
    id: 'profile_selector_selecting',
    defaultMessage: 'Selecting…',
  },
  selectTenantProfile: {
    id: 'profile_selector_select_tenant_profile',
    defaultMessage: 'Select {role} for {tenant}',
  },
  selectOrganizationProfile: {
    id: 'profile_selector_select_organization_profile',
    defaultMessage: 'Select {role} for {organization} in {tenant}',
  },
  loadFailed: {
    id: 'profile_selector_load_failed',
    defaultMessage: 'Your profiles could not be loaded.',
  },
  unknownApplication: {
    id: 'profile_selector_unknown_application',
    defaultMessage:
      'This application is not configured for profile access. Contact support.',
  },
  inactiveProfiles: {
    id: 'profile_selector_inactive_profiles',
    defaultMessage:
      'Your profiles for this application are inactive. Contact your administrator.',
  },
  noProfiles: {
    id: 'profile_selector_no_profiles',
    defaultMessage:
      'Your account has no profiles for this application. Contact your administrator.',
  },
  retry: {
    id: 'profile_selector_retry',
    defaultMessage: 'Try again',
  },
  logout: {
    id: 'profile_selector_logout',
    defaultMessage: 'Log out',
  },
});

interface OrganizationGroup {
  key: string;
  name: string;
  profiles: AllAuthUserProfile[];
}

interface TenantGroup {
  tenantId: string;
  tenant: string;
  tenantProfiles: AllAuthUserProfile[];
  organizations: OrganizationGroup[];
}

type SelectionSource = 'automatic' | 'manual';

interface PendingSelection {
  profile: AllAuthUserProfile;
  source: SelectionSource;
}

const getOrganizationKey = (
  organization: string,
  organizationId?: string,
): string => (organizationId ? `id:${organizationId}` : `name:${organization}`);

const groupProfiles = (
  profiles: AllAuthUserProfile[],
  collator: Intl.Collator,
  pendingProfile?: AllAuthUserProfile,
): TenantGroup[] => {
  const sortedProfiles = [...profiles].sort((a, b) => {
    const aHasOrganization = Boolean(a.organization);
    const bHasOrganization = Boolean(b.organization);

    return (
      collator.compare(a.tenant, b.tenant) ||
      collator.compare(a.tenant_id, b.tenant_id) ||
      Number(aHasOrganization) - Number(bHasOrganization) ||
      collator.compare(a.organization ?? '', b.organization ?? '') ||
      collator.compare(a.organization_id ?? '', b.organization_id ?? '') ||
      collator.compare(a.role, b.role) ||
      collator.compare(a.id, b.id)
    );
  });

  const tenants = new Map<string, TenantGroup>();

  for (const profile of sortedProfiles) {
    let tenantGroup = tenants.get(profile.tenant_id);
    if (!tenantGroup) {
      tenantGroup = {
        tenantId: profile.tenant_id,
        tenant: profile.tenant,
        tenantProfiles: [],
        organizations: [],
      };
      tenants.set(profile.tenant_id, tenantGroup);
    }

    if (!profile.organization) {
      tenantGroup.tenantProfiles.push(profile);
      continue;
    }

    const organizationKey = getOrganizationKey(
      profile.organization,
      profile.organization_id,
    );
    const organizationGroup = tenantGroup.organizations.find(
      (group) => group.key === organizationKey,
    );

    if (organizationGroup) {
      organizationGroup.profiles.push(profile);
    } else {
      tenantGroup.organizations.push({
        key: organizationKey,
        name: profile.organization,
        profiles: [profile],
      });
    }
  }

  if (pendingProfile) {
    const pendingTenantGroup = tenants.get(pendingProfile.tenant_id);
    if (pendingTenantGroup) {
      pendingTenantGroup.tenant = pendingProfile.tenant;

      if (pendingProfile.organization) {
        const pendingOrganizationKey = getOrganizationKey(
          pendingProfile.organization,
          pendingProfile.organization_id,
        );
        const pendingOrganizationGroup = pendingTenantGroup.organizations.find(
          (group) => group.key === pendingOrganizationKey,
        );

        if (pendingOrganizationGroup) {
          pendingOrganizationGroup.name = pendingProfile.organization;
        }
      }
    }
  }

  return [...tenants.values()];
};

const ProfileSelectorScreen: FC = () => {
  const intl = useIntl();
  const { enqueueSnackbar } = useSnackbar();
  const autoSelectProfile = useUserStore((state) => state.autoSelectProfile);
  const performLogout = useLogout();
  const selectingRef = useRef(false);
  const autoSelectAttemptedRef = useRef(false);
  const [pendingSelection, setPendingSelection] =
    useState<PendingSelection | null>(null);

  const profilesQuery = useAllAuthProfiles({
    onError: (err) => {
      if (axios.isAxiosError(err) && err.response?.status === 401) {
        startLogin().catch(console.error);
      }
    },
  });

  const activeProfiles = useMemo(
    () => getActiveProfiles(profilesQuery.data),
    [profilesQuery.data],
  );
  const displayedProfiles = useMemo(() => {
    if (!pendingSelection) {
      return activeProfiles;
    }

    const pendingProfile = pendingSelection.profile;
    const hasPendingProfile = activeProfiles.some(
      (profile) => profile.id === pendingProfile.id,
    );

    if (!hasPendingProfile) {
      return [...activeProfiles, pendingProfile];
    }

    return activeProfiles.map((profile) =>
      profile.id === pendingProfile.id ? pendingProfile : profile,
    );
  }, [activeProfiles, pendingSelection]);
  const collator = useMemo(() => new Intl.Collator(intl.locale), [intl.locale]);
  const tenantGroups = useMemo(
    () => groupProfiles(displayedProfiles, collator, pendingSelection?.profile),
    [collator, displayedProfiles, pendingSelection],
  );
  const refetchProfiles = profilesQuery.refetch;

  const onSelectProfile = useCallback(
    async (profile: AllAuthUserProfile, source: SelectionSource = 'manual') => {
      if (selectingRef.current) {
        return;
      }

      selectingRef.current = true;
      setPendingSelection({ profile, source });

      try {
        const token = await mintToken(profile.id);
        const store = useUserStore.getState();
        store.setAccessToken(token);
        store.setUserProfileId(profile.id);
        store.setRole(profile.role);
        store.setOrganization(profile.organization ?? null);
        window.location.replace(window.location.origin);
      } catch (err) {
        selectingRef.current = false;
        setPendingSelection(null);

        if (axios.isAxiosError(err) && err.response?.status === 401) {
          startLogin().catch(console.error);
          return;
        }

        enqueueSnackbar(
          <FormattedMessage
            id="profile_selector_error"
            defaultMessage="Login failed"
          />,
          { variant: 'error', preventDuplicate: true },
        );
        void refetchProfiles();
      }
    },
    [enqueueSnackbar, refetchProfiles],
  );

  useEffect(() => {
    if (
      autoSelectProfile &&
      activeProfiles.length === 1 &&
      !autoSelectAttemptedRef.current
    ) {
      autoSelectAttemptedRef.current = true;
      void onSelectProfile(activeProfiles[0], 'automatic');
    }
  }, [autoSelectProfile, activeProfiles, onSelectProfile]);

  const awaitingAutomaticSelection =
    profilesQuery.isSuccess &&
    autoSelectProfile &&
    activeProfiles.length === 1 &&
    !autoSelectAttemptedRef.current;
  const automaticSelectionProfile =
    pendingSelection?.source === 'automatic'
      ? pendingSelection.profile
      : awaitingAutomaticSelection
      ? activeProfiles[0]
      : null;
  const selectingProfileId = pendingSelection?.profile.id ?? null;
  const loadErrorStatus = axios.isAxiosError(profilesQuery.error)
    ? profilesQuery.error.response?.status
    : undefined;

  if (automaticSelectionProfile) {
    return (
      <ProfileSelectorPage>
        <StateMessage>
          <FormattedMessage
            {...messages.automaticSelection}
            values={{ role: automaticSelectionProfile.role }}
          />
        </StateMessage>
      </ProfileSelectorPage>
    );
  }

  if (!pendingSelection && profilesQuery.isLoading) {
    return (
      <ProfileSelectorPage>
        <StateMessage>
          <FormattedMessage {...messages.loading} />
        </StateMessage>
      </ProfileSelectorPage>
    );
  }

  if (!pendingSelection && profilesQuery.isError && loadErrorStatus === 404) {
    return (
      <ProfileSelectorPage>
        <StateMessage
          kind="alert"
          actions={<LogoutAction onLogout={performLogout} />}
        >
          <FormattedMessage {...messages.unknownApplication} />
        </StateMessage>
      </ProfileSelectorPage>
    );
  }

  if (
    !pendingSelection &&
    profilesQuery.isError &&
    activeProfiles.length === 0
  ) {
    return (
      <ProfileSelectorPage>
        <StateMessage
          kind="alert"
          actions={
            <div className="flex w-full flex-col gap-3">
              <Button
                className="w-full border-background-600 focus-visible:ring-text-950"
                loading={profilesQuery.isFetching}
                onClick={() => void refetchProfiles()}
              >
                <FormattedMessage {...messages.retry} />
              </Button>
              <LogoutAction
                disabled={profilesQuery.isFetching}
                onLogout={performLogout}
              />
            </div>
          }
        >
          <FormattedMessage {...messages.loadFailed} />
        </StateMessage>
      </ProfileSelectorPage>
    );
  }

  if (!pendingSelection && activeProfiles.length === 0) {
    const hasInactiveProfiles = (profilesQuery.data?.length ?? 0) > 0;

    return (
      <ProfileSelectorPage>
        <StateMessage
          kind={hasInactiveProfiles ? 'alert' : 'status'}
          actions={<LogoutAction onLogout={performLogout} />}
        >
          <FormattedMessage
            {...(hasInactiveProfiles
              ? messages.inactiveProfiles
              : messages.noProfiles)}
          />
        </StateMessage>
      </ProfileSelectorPage>
    );
  }

  return (
    <ProfileSelectorPage>
      <div className="flex w-full flex-col gap-8">
        <div className="flex flex-col gap-8">
          {tenantGroups.map((tenantGroup) => (
            <section key={tenantGroup.tenantId} className="flex flex-col gap-3">
              <h2 className="m-0 break-words text-lg font-semibold not-italic leading-6 tracking-normal text-text-950">
                {tenantGroup.tenant}
              </h2>
              <RoleList
                profiles={tenantGroup.tenantProfiles}
                selectingProfileId={selectingProfileId}
                onSelect={onSelectProfile}
              />
              {tenantGroup.organizations.map((organizationGroup) => (
                <div
                  key={organizationGroup.key}
                  className="ml-3 flex flex-col gap-3 border-t border-background-300 pt-3"
                >
                  <h3 className="m-0 flex items-start gap-2 break-words text-base font-semibold not-italic leading-6 tracking-normal text-text-700">
                    <BuildingOffice2Icon
                      aria-hidden="true"
                      className="mt-0.5 h-5 w-5 shrink-0"
                    />
                    <span className="min-w-0 break-words">
                      {organizationGroup.name}
                    </span>
                  </h3>
                  <RoleList
                    profiles={organizationGroup.profiles}
                    selectingProfileId={selectingProfileId}
                    onSelect={onSelectProfile}
                  />
                </div>
              ))}
            </section>
          ))}
        </div>
        <div className="pt-2">
          <LogoutAction
            disabled={Boolean(pendingSelection)}
            onLogout={performLogout}
          />
        </div>
      </div>
    </ProfileSelectorPage>
  );
};

const RoleList: FC<{
  profiles: AllAuthUserProfile[];
  selectingProfileId: string | null;
  onSelect: (
    profile: AllAuthUserProfile,
    source?: SelectionSource,
  ) => Promise<void>;
}> = ({ profiles, selectingProfileId, onSelect }) => {
  const intl = useIntl();

  if (profiles.length === 0) {
    return null;
  }

  return (
    <ul className="m-0 flex list-none flex-col gap-2 p-0">
      {profiles.map((profile) => {
        const isSelecting = selectingProfileId === profile.id;
        const accessibleName = profile.organization
          ? intl.formatMessage(messages.selectOrganizationProfile, {
              organization: profile.organization,
              role: profile.role,
              tenant: profile.tenant,
            })
          : intl.formatMessage(messages.selectTenantProfile, {
              role: profile.role,
              tenant: profile.tenant,
            });

        return (
          <li key={profile.id}>
            <Button
              className="h-auto min-h-12 w-full justify-between whitespace-normal border-background-600 px-4 py-3 text-left text-base font-semibold leading-6 tracking-normal focus-visible:ring-text-950"
              aria-label={accessibleName}
              aria-busy={isSelecting || undefined}
              disabled={selectingProfileId !== null}
              onClick={() => void onSelect(profile)}
            >
              <span className="min-w-0 flex-1 break-words">{profile.role}</span>
              {isSelecting && (
                <span className="shrink-0 text-sm font-normal text-text-700">
                  <FormattedMessage {...messages.selecting} />
                </span>
              )}
            </Button>
            {isSelecting && (
              <span role="status" aria-live="polite" className="sr-only">
                <FormattedMessage {...messages.selecting} />
              </span>
            )}
          </li>
        );
      })}
    </ul>
  );
};

const LogoutAction: FC<{
  disabled?: boolean;
  onLogout: () => void;
}> = ({ disabled = false, onLogout }) => (
  <Button
    variant="ghost"
    className="w-full font-normal text-text-700 focus-visible:ring-text-950"
    disabled={disabled}
    onClick={onLogout}
  >
    <FormattedMessage {...messages.logout} />
  </Button>
);

const StateMessage: FC<{
  actions?: ReactNode;
  children: ReactNode;
  kind?: 'alert' | 'status';
}> = ({ actions, children, kind = 'status' }) => (
  <div className="flex w-full flex-col items-center gap-6 text-center">
    <div
      role={kind}
      aria-live={kind === 'status' ? 'polite' : undefined}
      className="max-w-prose text-base leading-6 text-text-950"
    >
      {children}
    </div>
    {actions}
  </div>
);

const ProfileSelectorPage: FC<{
  children: ReactNode;
}> = ({ children }) => (
  <main className="flex h-full min-h-full w-full flex-col items-center gap-16 overflow-y-auto p-16 sm:gap-32 sm:p-32">
    <img
      src="/hållkoll.png"
      alt="HållKoll"
      className="h-16 max-w-full object-contain"
    />
    <h1 className="m-0 text-center text-4xl font-semibold not-italic leading-10 tracking-normal text-text-950">
      <FormattedMessage {...messages.heading} />
    </h1>
    <div className="flex w-full max-w-md flex-col">{children}</div>
  </main>
);

export default ProfileSelectorScreen;
